import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import RiskLevel from '../components/RiskLevel';
import ReadinessScore from '../components/ReadinessScore';
import CrisisStatus from '../components/CrisisStatus';

export default function Dashboard() {
  const readinessScore = 74;

  return (
    <div className="space-y-8">
      {/* Risk Level section */}
      <RiskLevel />

      {/* Readiness Score and Crisis Status */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
            Readiness Score
          </h3>
          <ReadinessScore score={readinessScore} />
        </div>
        <div>
          <h3 className="text-base font-semibold leading-6 text-gray-900 mb-4">
            Crisis Status
          </h3>
          <CrisisStatus status="lih-assigned" />
        </div>
      </div>

      {/* Last 30 Days */}
      <div>
        <h3 className="text-base font-semibold leading-6 text-gray-900">Last 30 Days</h3>
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Last 30 Days content remains the same */}
          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <ArrowUpIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Incidents</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">12</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-red-600">
                <ArrowUpIcon className="h-5 w-5 flex-shrink-0 self-center text-red-500" aria-hidden="true" />
                <span className="sr-only">Increased by</span>
                25%
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <ArrowDownIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Avg Response Time</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">45m</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                <ArrowDownIcon className="h-5 w-5 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
                <span className="sr-only">Decreased by</span>
                15%
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <ArrowUpIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Team Readiness</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">92%</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                <ArrowUpIcon className="h-5 w-5 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
                <span className="sr-only">Increased by</span>
                5%
              </p>
            </dd>
          </div>

          <div className="relative overflow-hidden rounded-lg bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute rounded-md bg-indigo-500 p-3">
                <ArrowUpIcon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">Compliance Score</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">98%</p>
              <p className="ml-2 flex items-baseline text-sm font-semibold text-green-600">
                <ArrowUpIcon className="h-5 w-5 flex-shrink-0 self-center text-green-500" aria-hidden="true" />
                <span className="sr-only">Increased by</span>
                2%
              </p>
            </dd>
          </div>
        </dl>
      </div>

      {/* Ownership & Governance */}
      <div>
        <h3 className="text-base font-semibold leading-6 text-gray-900">Ownership & Governance</h3>
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
          {/* Rest of the Ownership & Governance content remains the same */}
          {/* Balance Scorecard */}
          <div className="bg-white shadow rounded-lg p-6">
            <h4 className="text-lg font-medium mb-4">Balance Scorecard</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Incident Response Time</span>
                <div className="flex items-center">
                  <span className="font-medium">45min</span>
                  <span className="text-gray-500 text-sm ml-2">Target: 30min</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Team Readiness</span>
                <div className="flex items-center">
                  <span className="font-medium">92%</span>
                  <span className="text-gray-500 text-sm ml-2">Target: 95%</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Plan Coverage</span>
                <div className="flex items-center">
                  <span className="font-medium">85%</span>
                  <span className="text-gray-500 text-sm ml-2">Target: 100%</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span>Compliance Score</span>
                <div className="flex items-center">
                  <span className="font-medium">98%</span>
                  <span className="text-gray-500 text-sm ml-2">Target: 100%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Reports */}
          <div className="bg-white shadow rounded-lg p-6">
            <h4 className="text-lg font-medium mb-4">Reports</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Monthly Risk Assessment</span>
                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">completed</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Quarterly Compliance Review</span>
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">pending</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Annual Security Audit</span>
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">upcoming</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Incident Response Summary</span>
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">in-progress</span>
              </div>
            </div>
          </div>

          {/* Action Items */}
          <div className="bg-white shadow rounded-lg p-6">
            <h4 className="text-lg font-medium mb-4">Action Items</h4>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Update Emergency Contact List</p>
                  <p className="text-sm text-gray-500">Due: 2024-01-25</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">pending</span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Review Communication Protocols</p>
                  <p className="text-sm text-gray-500">Due: 2024-01-25</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">in-progress</span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Schedule Team Training</p>
                  <p className="text-sm text-gray-500">Due: 2024-01-30</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">pending</span>
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Update Response Templates</p>
                  <p className="text-sm text-gray-500">Due: 2024-02-15</p>
                </div>
                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">not-started</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}